import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { client } from '../../api/client';
import { AlertModal } from '../../components/ConfirmModal';
import { GlobalFooter } from '../../components/GlobalFooter';
import { formatDate } from '../../utils/dateFormat';
import { ChevronDown } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const StarIcon = ({ filled }: { filled: boolean }) => (
  <svg className={`w-5 h-5 ${filled ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

interface Review {
  id: string;
  name: string;
  rating: number;
  comment: string;
  image_url: string | null;
  created_at: string;
  admin_reply?: string | null;
  replied_at?: string | null;
}

function ReviewCard({ review }: { review: Review }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {review.image_url && (
        <img
          src={review.image_url}
          alt="Review"
          className="h-48 w-full object-cover"
        />
      )}

      <div className="p-6">
        <div className="flex items-center mb-2">
          {[1, 2, 3, 4, 5].map((s) => (
            <StarIcon key={s} filled={s <= review.rating} />
          ))}
        </div>

        <p className="text-gray-900 font-semibold mb-1">
          {review.name}
        </p>

        <p className="text-gray-600 text-sm mb-3 leading-relaxed">
          {review.comment}
        </p>

        <p className="text-gray-400 text-xs">
          {new Date(review.created_at).toLocaleString("en-GB")}
        </p>

        {review.admin_reply && (
          <div className="mt-4 bg-blue-50 border-l-4 border-nhs-blue p-3 rounded-r-lg">
            <div className="text-sm">
              <span className="font-bold text-nhs-blue">
                Talis'on Services:
              </span>{" "}
              <span className="text-gray-700">
                {review.admin_reply}
              </span>
            </div>
            {review.replied_at && (
              <p className="text-gray-400 text-xs mt-1">
                {review.replied_at && 
  new Date(review.replied_at).toLocaleString("en-GB")
}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// FAQ data
const FAQ_DATA = [
  {
    q: "I've applied many times and keep getting rejected. Can you really help?",
    a: "Yes — that's exactly why we exist. Many qualified candidates receive repeated \"unfortunately\" responses because they are not presenting their experience the way recruiters expect. We show you how to align your application with high standards and prepare you to confidently perform at interview."
  },
  {
    q: "What makes Talis'on Services different from applying on my own?",
    a: "Most applicants submit applications without structured guidance. We provide:\n\n• Personalised coaching\n• Mock interview practice\n• Document review and feedback\n• Practical industry-focused strategies\n\nWe don't just give advice — we guide you step-by-step."
  },
  {
    q: "Is this suitable if I'm applying for HCA, Nursing or Admin roles?",
    a: "Absolutely. Whether you are applying for:\n\n• Healthcare Assistant (HCA)\n• Nursing roles\n• Administrative roles\n• Other healthcare positions\n\nOur support is tailored to the specific role you're targeting."
  },
  {
    q: "I'm experienced and qualified. Why am I not getting offers?",
    a: "Experience alone is not always enough. Recruiters often looks for:\n\n• Structured answers\n• Evidence-based examples\n• Understanding of values\n• Strong interview delivery\n\nWe help you translate your experience into what employers are actually looking for."
  },
  {
    q: "What happens after I sign up?",
    a: "After your account is approved:\n\n• You receive structured onboarding support\n• You gain access to training sessions\n• You receive the required form to complete\n• We begin guiding you through preparation\n\nEverything is organised to keep you on track."
  },
  {
    q: "Is there ongoing support?",
    a: "Yes. We provide continuous guidance, feedback, and resources so you are never navigating the process alone."
  },
  {
    q: "What if I'm nervous about interviews?",
    a: "That's completely normal. Many strong candidates struggle with interview confidence. Our mock interviews and coaching sessions are designed to build your confidence and help you answer questions clearly and professionally."
  },
  {
    q: "How do I get started?",
    a: "Simply sign up, complete your onboarding steps, and we will guide you through the rest. Your journey to securing your dream role starts with one decision — taking action."
  },
];

// Services data
const SERVICES_DATA = [
  {
    title: "Guidance Into The Right Industry Pathway",
    description: "We help you identify the most suitable career pathway based on your qualifications, experience, and long-term goals. Whether you're looking for new path or looking to transition roles, we ensure you're headed in the right direction.",
    icon: (
      <svg className="w-8 h-8 text-nhs-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
      </svg>
    ),
  },
  {
    title: "Job Role Opportunities",
    description: "Access dedicated support and curated opportunities for your dream roles. We guide you through the application process, help you understand role specific requirements, and prepare you to stand out among other candidates.",
    icon: (
      <svg className="w-8 h-8 text-nhs-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    title: "Application & Compliance Support",
    description: "From guiding you in completing application forms correctly to ensuring full compliance with industry standards, our team reviews your documents, provides actionable feedback, and tracks yours submitted applications that get noticed.",
    icon: (
      <svg className="w-8 h-8 text-nhs-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    title: "Weekly General Training Sessions",
    description: "Join our structured weekly training sessions covering several essential topics including governance and data security, customer service skills, DSE, health and safety, mental health awareness, safeguarding, equality and diversity, and more. Stay informed and interview-ready at all times.",
    icon: (
      <svg className="w-8 h-8 text-nhs-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
  },
  {
    title: "1:1 Interview Preparation Sessions",
    description: "Book personalised one-on-one coaching sessions to refine your interview technique, practise with realistic scenarios, and receive expert feedback tailored to your target role and experience level.",
    icon: (
      <svg className="w-8 h-8 text-nhs-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
  },
  {
    title: "Audio & Video Library",
    description: "Access a comprehensive library of training recordings, mock group sessions, and educational content tailored to specific roles. Learn at your own pace with on-demand resources available anytime.",
    icon: (
      <svg className="w-8 h-8 text-nhs-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    title: "Step-by-step Onboarding Support",
    description: "From the moment you sign up, we provide clear, structured onboarding to get you started. Every step is organised so you always know what to do next — no confusion, no guesswork, just guided progress.",
    icon: (
      <svg className="w-8 h-8 text-nhs-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    ),
  },
];

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 sm:p-6 text-left bg-white hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm sm:text-base font-semibold text-gray-900 pr-4">{q}</span>
        <ChevronDown className={`h-5 w-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${open ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-sm sm:text-base text-gray-600 leading-relaxed whitespace-pre-line">
          {a}
        </div>
      </div>
    </div>
  );
}

export function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [session, setSession] = useState<any>(null);
  const [reviewForm, setReviewForm] = useState({ authorName: '', rating: 5, comment: '', imageUrl: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [alertMsg, setAlertMsg] = useState('');

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setMobileMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, []);

  useEffect(() => {
  supabase.auth.getSession().then(({ data }) => {
    setSession(data.session);
  });

  const { data: listener } = supabase.auth.onAuthStateChange(
    (_event, session) => {
      setSession(session);
    }
  );

  return () => {
    listener.subscription.unsubscribe();
  };
}, []);

  useEffect(() => {
    loadReviews();
  }, []);

  async function loadReviews() {
  try {
    const { data: reviewsData, error } = await supabase
      .from("reviews")
      .select(`
        id,
        rating,
        comment,
        image_url,
        created_at,
        admin_reply,
        replied_at,
        user_id,
        profiles:profiles!reviews_user_id_fkey (
          full_name
        )
      `)
      .eq("is_hidden", false)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Supabase error:", error);
      throw error;
    }

    console.log("Joined data:", reviewsData);

    const formatted = (reviewsData || []).map((r: any) => ({
      id: r.id,
      rating: r.rating,
      comment: r.comment,
      image_url: r.image_url,
      created_at: r.created_at,
      admin_reply: r.admin_reply,
      replied_at: r.replied_at,
      name: r.profiles?.full_name || "Anonymous",
    }));

    setReviews(formatted);

  } catch (err) {
    console.error("Failed to load reviews:", err);
  } finally {
    setReviewsLoading(false);
  }
}

  function handleNavClick(e: React.MouseEvent<HTMLAnchorElement>, targetId: string) {
    e.preventDefault();
    setMobileMenuOpen(false);
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  async function handleFileUpload(
  e: React.ChangeEvent<HTMLInputElement>
) {
  const file = e.target.files?.[0];
  if (!file) return;

  setUploading(true);

  try {
    const filePath = `${Date.now()}-${file.name}`;

    const { error } = await supabase.storage
      .from("reviews")
      .upload(filePath, file);

    if (error) throw error;

    const { data } = supabase.storage
      .from("reviews")
      .getPublicUrl(filePath);

    setReviewForm({
      ...reviewForm,
      imageUrl: data.publicUrl,
    });
  } catch (err) {
    console.error(err);
    setAlertMsg("Upload failed. Please try again.");
  } finally {
    setUploading(false);
  }
}

  async function handleReviewSubmit(e: React.FormEvent) {
  e.preventDefault();

  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) {
    setAlertMsg("You must be logged in to submit a review.");
    return;
  }

  const { data: profile, error: profileError } = await supabase
    .from("profiles")
    .select("full_name, role")
    .eq("id", userData.user.id)
    .single();

  if (profileError || !profile) {
    setAlertMsg("Profile not found.");
    return;
  }

  if (profile.role !== "client") {
    setAlertMsg("Only clients can submit reviews.");
    return;
  }

  setSubmitting(true);

  const { error } = await supabase.from("reviews").insert({
    user_id: userData.user.id,
    rating: reviewForm.rating,
    comment: reviewForm.comment,
    image_url: reviewForm.imageUrl || null,
  });

  if (error) {
    console.error(error);
    setAlertMsg("Failed to submit review.");
  } else {
    setSubmitted(true);
    setReviewForm({
      authorName: "",
      rating: 5,
      comment: "",
      imageUrl: "",
    });
    loadReviews();
  }

  setSubmitting(false);
}

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="bg-white shadow-sm fixed w-full top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-xl sm:text-2xl font-bold text-nhs-blue flex-shrink-0">
              TALIS'ON SERVICES
            </Link>

            <div className="hidden md:flex items-center space-x-6">
              <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-gray-600 hover:text-nhs-blue text-sm font-semibold transition-colors">Home</a>
              <a href="#services" onClick={(e) => handleNavClick(e, 'services')} className="text-gray-600 hover:text-nhs-blue text-sm font-semibold transition-colors">Services</a>
              <a href="#about" onClick={(e) => handleNavClick(e, 'about')} className="text-gray-600 hover:text-nhs-blue text-sm font-semibold transition-colors">About</a>
              <a href="#faqs" onClick={(e) => handleNavClick(e, 'faqs')} className="text-gray-600 hover:text-nhs-blue text-sm font-semibold transition-colors">FAQs</a>
              <a href="#reviews" onClick={(e) => handleNavClick(e, 'reviews')} className="text-gray-600 hover:text-nhs-blue text-sm font-semibold transition-colors">Reviews</a>
              {session ? (
  <>
    <Link
      to="/dashboard"
      className="text-gray-600 hover:text-nhs-blue text-sm  font-semibold  transition-colors"
    >
      Profile
    </Link>
    <button
      onClick={async () => await supabase.auth.signOut()}
      className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-nhs-blue text-white font-semibold rounded-lg hover:bg-nhs-blue transition-colors text-sm sm:text-base"
    >
      Sign Out
    </button>
  </>
) : (
  <>
    <Link
      to="/login"
      className="text-gray-600 hover:text-nhs-blue text-sm font-medium transition-colors"
    >
      Login
    </Link>
    <Link
      to="/register"
      className="bg-nhs-blue text-white hover:bg-nhs-dark px-5 py-2.5 rounded-lg text-sm font-medium transition-colors"
    >
      Sign Up
    </Link>
  </>
)}
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-4 py-3 space-y-2">
              <a href="#top" onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="block py-2.5 px-3 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium">Home</a>
              <a href="#services" onClick={(e) => handleNavClick(e, 'services')} className="block py-2.5 px-3 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium">Services</a>
              <a href="#about" onClick={(e) => handleNavClick(e, 'about')} className="block py-2.5 px-3 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium">About</a>
              <a href="#faqs" onClick={(e) => handleNavClick(e, 'faqs')} className="block py-2.5 px-3 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium">FAQs</a>
              <a href="#reviews" onClick={(e) => handleNavClick(e, 'reviews')} className="block py-2.5 px-3 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium">Reviews</a>
              {session ? (
  <>
    <Link
      to="/dashboard"
      onClick={() => setMobileMenuOpen(false)}
      className="block py-2.5 px-3 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium"
    >
      Profile
    </Link>
    <button
      onClick={async () => {
        await supabase.auth.signOut();
        setMobileMenuOpen(false);
      }}
      className="block w-full text-left py-2.5 px-3 text-red-600 hover:bg-gray-50 rounded-lg text-sm font-medium"
    >
      Sign Out
    </button>
  </>
) : (
  <>
    <Link
      to="/login"
      onClick={() => setMobileMenuOpen(false)}
      className="block py-2.5 px-3 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium"
    >
      Login
    </Link>
    <Link
      to="/register"
      onClick={() => setMobileMenuOpen(false)}
      className="block py-2.5 px-3 bg-nhs-blue text-white text-center rounded-lg text-sm font-medium mt-2"
    >
      Sign Up
    </Link>
  </>
)}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-16">
        <div className="relative w-full">
          <div className="relative min-h-[60vh] sm:min-h-[70vh] lg:min-h-[80vh]">
            <img
              src="/assets/hero-professional.png"
              alt="Professional career consultants"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
            <div className="relative z-10 flex items-center min-h-[60vh] sm:min-h-[70vh] lg:min-h-[80vh]">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-12 sm:py-16 lg:py-20">
                <div className="max-w-xl lg:max-w-2xl">
                  <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight">
                    Secure Your Dream Job
                  </h1>
                  <p className="mt-4 sm:mt-6 text-base sm:text-lg md:text-xl text-gray-200 leading-relaxed max-w-lg">
                    Talison Services is dedicated to supporting individuals who are navigating their professional journey within the healthcare sector.
                  </p>
                  <p className="mt-4 sm:mt-6 text-base sm:text-lg md:text-xl text-gray-200 leading-relaxed max-w-lg">
                    Join TALIS'ON SERVICES for expert coaching, guaranteed interviews, and comprehensive support tailored for professionals.
                  </p>
                  <p className="mt-6 text-xl text-gray-100 max-w-3xl">
                    We understand that starting or advancing a healthcare career can feel overwhelming. Between understanding requirements, preparing for roles, building confidence, and meeting professional standards, many capable individuals struggle simply because they lack clear direction and structured guidance.
                  </p>
                  <p className="mt-6 text-xl text-gray-100 max-w-3xl">
                    Talison Services was created to provide that structure.
                  </p>
                  <p className="mt-6 text-xl text-gray-100 max-w-3xl">
                    Our mission is to simplify complex processes and provide clear, practical support that empowers individuals to move forward confidently in their careers.
                  </p>
                  <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <Link
                      to="/register"
                      className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-nhs-blue text-white font-semibold rounded-lg hover:bg-nhs-dark transition-colors text-sm sm:text-base"
                    >
                      Get Started
                    </Link>
                    <a
                      href="#services"
                      onClick={(e) => handleNavClick(e, 'services')}
                      className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-white/20 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/30 transition-colors border border-white/30 text-sm sm:text-base"
                    >
                      View Services
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Services Section - 7 Categories */}
      <section id="services" className="py-16 sm:py-20 lg:py-24 bg-gray-50 scroll-mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Our Services</h2>
            <p className="mt-3 sm:mt-4 text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive Career Support — Everything you need to succeed in your application journey.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {SERVICES_DATA.map((service, i) => (
              <div key={i} className={`bg-white rounded-xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow border border-gray-100 ${i === 6 ? 'sm:col-span-2 lg:col-span-1' : ''}`}>
                <div className="w-14 h-14 rounded-xl bg-nhs-pale flex items-center justify-center mb-5">
                  {service.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 text-sm sm:text-base leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-16 sm:py-20 lg:py-24 bg-white scroll-mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">About Us</h2>
          </div>
          <div className="space-y-6 text-gray-700 text-base sm:text-lg leading-relaxed">
            <p>
              At Talis'on Services, we truly understand the frustration and disappointment many professionals experience when applying for roles. Highly experienced and qualified candidates often submit countless applications, only to receive the same response — "Unfortunately, you have not been successful on this occasion."
            </p>
            <p>
              We know how discouraging that can feel.
            </p>
            <p>
              That is exactly why Talis'on Services exists — to provide the guidance, structure, and expert support you need to turn those rejections into interview invitations and job offers. We help you understand what recruiters are really looking for, how to present your experience effectively, and how to confidently perform at interview.
            </p>
            <p>
              Whether you are aiming for a Healthcare Assistant (HCA) role, Nursing position, Administrative role, or any other opportunity within the private or public sector, we are here to support and guide you every step of the way.
            </p>
            <p className="font-semibold text-nhs-blue text-lg sm:text-xl">
              Your success is our priority, and we are committed to helping you secure the role you deserve.
            </p>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section id="faqs" className="py-16 sm:py-20 lg:py-24 bg-gray-50 scroll-mt-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Frequently Asked Questions</h2>
            <p className="mt-3 sm:mt-4 text-base sm:text-lg text-gray-600">
              Find answers to common questions about our services.
            </p>
          </div>
          <div className="space-y-3">
            {FAQ_DATA.map((faq, i) => (
              <FAQItem key={i} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-us" className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                Expert Guidance for Healthcare Professionals
              </h2>
              <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-6 sm:mb-8">
                At TALIS'ON SERVICES, we understand the nuances of industry recruitment. Our team consists of experienced professionals who know exactly what hiring managers are looking for.
              </p>
              <ul className="space-y-4">
                {[
                  'Extensive tailored support.',
                  'Transparent progress for peace of mind.',
                  'Community feedback system to learn from peers.',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700 text-sm sm:text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-nhs-blue rounded-2xl p-8 sm:p-10 lg:p-12">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-8 text-center">Our Track Record</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                  <div className="text-4xl sm:text-5xl font-bold text-white mb-2">64</div>
                  <div className="text-sm sm:text-base text-blue-100">Certificate of Sponsorship Secured in 2024</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                  <div className="text-4xl sm:text-5xl font-bold text-white mb-2">68</div>
                  <div className="text-sm sm:text-base text-blue-100">Certificate of Sponsorship Secured in 2025</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories Section */}
      <section id="testimonials" className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Success Stories</h2>
            <p className="mt-3 sm:mt-4 text-base sm:text-lg text-gray-600">What Our Candidates Say</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                name: 'Modupe',
                role: 'Band 3 Support Worker',
                quote: "I was struggling with NHS application for months. The mock sessions at Talison gave me the confidence I needed. I secured my dream job within 6 weeks! Talison will hold your hands from the beginning till you get your feet into NHS as an employee",
              },
              {
                name: 'Patricia.',
                role: 'Nursing Associate',
                quote: 'The 1-on-1 coaching was a game changer. My coach identified exactly where I was going wrong in my interviews. Highly recommended!',
              },
              {
                name: 'James.',
                role: 'Senior Clerical Officer',
                quote: 'From CV review to the final offer, the team was supportive every step of the way. The dashboard and community support made it so easy. Thank you Talison.',
              },
            ].map((t, i) => (
              <div key={i} className="bg-white rounded-xl p-6 sm:p-8 shadow-sm border border-gray-100">
                <div className="flex items-center mb-4">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <StarIcon key={s} filled={true} />
                  ))}
                </div>
                <blockquote className="text-gray-600 text-sm sm:text-base leading-relaxed mb-6 italic">
                  "{t.quote}"
                </blockquote>
                <div>
                  <div className="font-semibold text-gray-900">{t.name}</div>
                  <div className="text-sm text-nhs-blue">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="py-16 sm:py-20 lg:py-24 bg-white scroll-mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Reviews</h2>
            <p className="mt-3 sm:mt-4 text-base sm:text-lg text-gray-600">
              Read what our clients say, or share your own experience below.
            </p>
          </div>

          {reviewsLoading ? (
            <div className="text-center py-12">
              <div className="text-gray-400 font-medium">Loading reviews...</div>
            </div>
          ) : reviews.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-16">
              {reviews.map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 mb-16">
              <p className="text-gray-400">No reviews yet. Be the first to share your experience!</p>
            </div>
          )}

          {/* Review Form */}
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10 sm:mb-12">
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Share Your Experience</h3>
              <p className="mt-3 sm:mt-4 text-base sm:text-lg text-gray-600">
                We value your feedback. Let us know how we helped you.
              </p>
            </div>

            {submitted ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 sm:p-8 text-center">
                <svg className="w-12 h-12 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-lg font-semibold text-green-800 mb-2">Thank you for your review!</h3>
                <p className="text-green-600 text-sm sm:text-base">Your review has been submitted and is now visible.</p>
                <button onClick={() => setSubmitted(false)} className="mt-4 text-nhs-blue font-medium text-sm hover:underline">
                  Submit another review
                </button>
              </div>
            ) : (
              <form onSubmit={handleReviewSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Your Name</label>
                  <input
                    type="text"
                    required
                    value={reviewForm.authorName}
                    onChange={(e) => setReviewForm({ ...reviewForm, authorName: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-nhs-blue focus:border-transparent outline-none transition-all"
                    placeholder="Enter your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Rating</label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                        className="p-1 transition-transform hover:scale-110"
                      >
                        <StarIcon filled={star <= reviewForm.rating} />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Your Review</label>
                  <textarea
                    required
                    rows={4}
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-nhs-blue focus:border-transparent outline-none transition-all resize-none"
                    placeholder="Share your experience..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Photo (Optional)</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-nhs-blue file:text-white hover:file:bg-nhs-dark"
                  />
                  {uploading && <p className="text-xs text-gray-400 mt-1">Uploading image...</p>}
                  {reviewForm.imageUrl && !uploading && <p className="text-xs text-green-600 mt-1">✓ Image uploaded</p>}
                </div>
                <button
                  type="submit"
                  disabled={submitting || uploading}
                  className="w-full bg-nhs-blue text-white font-semibold py-3 px-6 rounded-lg hover:bg-nhs-dark transition-colors disabled:opacity-50 text-sm sm:text-base"
                >
                  {submitting ? 'Submitting...' : 'Submit Review'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <GlobalFooter />
      <AlertModal open={!!alertMsg} title="Notice" message={alertMsg} onClose={() => setAlertMsg('')} variant="danger" />
    </div>
  );
}
